import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';

import { AutomationStatusEnum } from '../../../types/automations';
import { getAutomationStatusLabel } from '../../../utils/automations';
import { TFieldSaveState } from './FieldSaveIndicator';

interface IProps {
  status: string;
  subject: string;
  saveState?: TFieldSaveState;
}

const ICON_SX = { fontSize: 20 };

const renderIcon = (status: string) => {
  switch (status) {
    case AutomationStatusEnum.Active:
      return <CheckCircleIcon sx={{ ...ICON_SX, color: 'success.main' }} />;
    case AutomationStatusEnum.NotActive:
      return <PauseCircleIcon sx={{ ...ICON_SX, color: 'warning.main' }} />;
    case AutomationStatusEnum.Failed:
      return <ErrorIcon sx={{ ...ICON_SX, color: 'error.main' }} />;
    default:
      return <HourglassEmptyIcon sx={{ ...ICON_SX, color: 'text.disabled' }} />;
  }
};

const renderSaveState = (saveState?: TFieldSaveState) => {
  if (saveState === 'saving') {
    return <CircularProgress size={16} aria-label="Saving status" />;
  }

  if (saveState === 'saved') {
    return (
      <Typography variant="body2" sx={{ color: 'success.main' }}>
        Saved
      </Typography>
    );
  }

  return null;
};

export const AutomationStatusBadge: React.FC<IProps> = ({ status, subject, saveState }) => (
  <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: '20px' }}>
      Status
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {renderIcon(status)}
      <Typography variant="body1">{getAutomationStatusLabel(status, subject).message}</Typography>
      <Box aria-live="polite" sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 20 }}>
        {renderSaveState(saveState)}
      </Box>
    </Box>
  </Box>
);

export default AutomationStatusBadge;
